import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Klydo } from "../../../context/klydo/KlydoTypes";
import { GiftCategory, KlydoGiftProps } from "../../../api/gift";
import useGiftCategories from "../hooks/useGiftCategories";
import axios from "axios";
import {
  captureError,
  useHistory,
  getDeviceAppropriateAnimationUrl,
} from "../../../utils";
import { ALLOWED_ROLES, SplashScreen } from "../../../LoginView/SplashScreen";
import { getToken } from "../../../api/baseRequest";
import AppContext from "../../../AppContext";
import steps from "../Steps/steps";

export type GiftContextType = {
  selectedOccasion?: GiftCategory;
  selectedKlydoGreet?: Klydo;
  customKlydoId?: string;
  klydoGiftProps: KlydoGiftProps;
  next: () => void;
  back: () => void | undefined;
  loading?: boolean;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedOccasion: React.Dispatch<React.SetStateAction<GiftCategory>>;
  setSelectedKlydoGreet: React.Dispatch<React.SetStateAction<Klydo>>;
  setCustomKlydoId: React.Dispatch<React.SetStateAction<string>>;
  setKlydoGiftProps: (
    props: Partial<KlydoGiftProps & { clockId: string }>,
  ) => void;
  resetSenderImageUrl: () => void;
  giftCategories: GiftCategory[];
  admin: boolean;
};
const GiftContext = createContext<GiftContextType>({} as GiftContextType);

export const useGiftContext = () => useContext(GiftContext);

export const GiftContextProvider = ({ children }) => {
  const defaultPhoto =
    "https://res.cloudinary.com/klydoclock/image/upload/profile/defaultPhoto_mc2iv6.png";
  const { data: giftCategories, isLoading } = useGiftCategories();
  const [admin, setAdmin] = useState(false);
  const { user } = useContext(AppContext);
  const { setQueryParam, search, goBack } = useHistory();

  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);

  const [selectedOccasion, setSelectedOccasion] = useState<GiftCategory>();
  const [selectedKlydoGreet, setSelectedKlydoGreet] = useState<Klydo>();
  const [klydoGiftProps, setKlydoGiftPropsState] = useState<
    KlydoGiftProps & { clockId: string }
  >({
    senderImageUrl: defaultPhoto,
    senderName: "",
    greetingContent: "",
    giftId: "",
    orderName: "",
    clockId: "",
    klydoId: "",
  });

  const setKlydoGiftProps = (
    props: Partial<KlydoGiftProps & { clockId: string }>,
  ) => {
    setKlydoGiftPropsState((prev) => ({ ...prev, ...props }));
  };

  const resetSenderImageUrl = () => {
    setKlydoGiftPropsState((prev) => ({
      ...prev,
      senderImageUrl: defaultPhoto,
    }));
  };

  const [customKlydoId, setCustomKlydoId] = useState<string>();

  const prefetchController = useRef(new AbortController());
  const fetchAll = async () => {
    if (initializing) {
      prefetchController.current = new AbortController();
      try {
        await Promise.all(
          giftCategories?.map(async (category) => {
            await Promise.all(
              category.klydos.slice(0, 5).map(async (klydo) => {
                try {
                  const animationUrl = getDeviceAppropriateAnimationUrl(klydo);
                  await axios.get(animationUrl, {
                    signal: prefetchController.current.signal,
                  });
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (error: any) {
                  if (error.name === "CanceledError") {
                    return;
                  } else {
                    captureError(error, {
                      klydo: JSON.stringify(klydo),
                    });
                  }
                }
              }),
            );
          }),
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.name === "CanceledError") {
          return;
        } else {
          captureError(error, {
            categories: JSON.stringify(giftCategories),
          });
        }
      } finally {
        setInitializing(false);
      }
    }
  };
  useEffect(() => {
    const controller = new AbortController();
    if (selectedOccasion) {
      prefetchController.current.abort();
    }
    return () => {
      controller.abort();
    };
  }, [selectedOccasion, giftCategories]);

  useEffect(() => {
    getToken().then((token) => {
      if (token && ALLOWED_ROLES.includes(user.role)) setAdmin(true);
    });
  }, []);

  useEffect(() => {
    if (klydoGiftProps?.senderImageUrl) {
      axios.get(klydoGiftProps.senderImageUrl);
    }
  }, [klydoGiftProps]);

  useEffect(() => {
    if (!isLoading && giftCategories?.length > 0) {
      fetchAll();
    }
  }, [isLoading, giftCategories]);

  useEffect(() => {
    if (
      klydoGiftProps?.klydoId &&
      giftCategories?.length > 0 &&
      !selectedOccasion
    ) {
      const categoryWithKlydo = giftCategories.find((category) =>
        category.klydos?.some((klydo) => klydo.id === klydoGiftProps.klydoId),
      );
      if (categoryWithKlydo) {
        const foundKlydo = categoryWithKlydo.klydos?.find(
          (klydo) => klydo.id === klydoGiftProps.klydoId,
        );
        if (foundKlydo) {
          setSelectedOccasion(categoryWithKlydo);
          setSelectedKlydoGreet(foundKlydo);
        }
      }
    }
  }, [klydoGiftProps?.klydoId, giftCategories, selectedOccasion]);
  if (isLoading) {
    return SplashScreen();
  }

  const step = search.step;
  const activeStep = steps.findIndex((s) => s.id === step);

  const next = () => {
    setQueryParam("step", steps[activeStep + 1].id, true);
  };

  const canGoBack = history?.state?.idx > 0;

  const back = () => {
    goBack();
  };

  return (
    <GiftContext.Provider
      value={{
        selectedOccasion,
        selectedKlydoGreet,
        klydoGiftProps,
        setSelectedOccasion,
        next,
        back: canGoBack ? back : undefined,
        setSelectedKlydoGreet,
        setKlydoGiftProps,
        resetSenderImageUrl,
        giftCategories,
        admin,
        customKlydoId,
        setCustomKlydoId,
        loading,
        setLoading,
      }}
    >
      {children}
    </GiftContext.Provider>
  );
};
